import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, PaperPlaneRight, XCircle, Check, Key } from '@phosphor-icons/react';
import { toast } from 'sonner';

const ALL_PERMISSIONS = ['action_center', 'stock', 'settlement', 'finance', 'delivery_orders', 'products', 'users', 'reports'];

export const UsersPage = () => {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [partners, setPartners] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const empty = { name: '', email: '', phone: '', role: 'finance', permissions: [], pharmacy_id: null, partner_id: null };
  const [newUser, setNewUser] = useState(empty);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [u, ph, pt] = await Promise.all([api.get('/users'), api.get('/pharmacies'), api.get('/partners')]);
      setUsers(u.data);
      setPharmacies(ph.data);
      setPartners(pt.data);
    } catch (e) { console.error(e); }
  };

  const handleInvite = async () => {
    if (!newUser.name) return toast.error('Name is required');
    if (!newUser.email && !newUser.phone) return toast.error('Email or phone is required');
    if (newUser.role === 'pharmacy' && !newUser.pharmacy_id) return toast.error('Please select a pharmacy');
    if (newUser.role === 'partner' && !newUser.partner_id) return toast.error('Please select a partner');
    try {
      await api.post('/users', newUser);
      toast.success('Invitation sent');
      setDialogOpen(false); setNewUser(empty); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to invite'); }
  };

  const handleResend = async (id) => {
    try { await api.post(`/users/${id}/resend-invitation`); toast.success('Invitation resent'); } catch { toast.error('Failed'); }
  };
  const handleToggle = async (u) => {
    try {
      const endpoint = u.status === 'active' ? 'deactivate' : 'activate';
      await api.post(`/users/${u.id}/${endpoint}`);
      toast.success(`User ${endpoint}d`);
      fetchData();
    } catch { toast.error('Failed'); }
  };
  const [tempPwDialog, setTempPwDialog] = useState({ open: false, user: null, password: '' });
  const [tempPwResult, setTempPwResult] = useState(null);
  const handleSetTempPassword = async () => {
    const u = tempPwDialog.user;
    if (!u) return;
    try {
      const res = await api.post(`/users/${u.id}/set-temp-password`, { password: tempPwDialog.password || null });
      setTempPwResult({ user: u, ...res.data });
      toast.success('Temporary password set');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const togglePermission = (perm) => setNewUser(prev => ({
    ...prev,
    permissions: prev.permissions.includes(perm) ? prev.permissions.filter(p => p !== perm) : [...prev.permissions, perm]
  }));

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope', color: '#1E3A8A' }}>{t('nav.users')}</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="invite-user-btn" style={{ backgroundColor: '#1E3A8A' }}><Plus size={16} className="mr-2" /> Invite User</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Invite New User</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Name *</Label><Input data-testid="new-user-name" value={newUser.name} onChange={(e) => setNewUser({...newUser, name: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Email</Label><Input data-testid="new-user-email" type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} /></div>
                <div><Label>Phone</Label><Input data-testid="new-user-phone" value={newUser.phone} onChange={(e) => setNewUser({...newUser, phone: e.target.value})} /></div>
              </div>
              <p className="text-xs text-slate-500 -mt-2">At least one contact method required. Email is used for invite emails; phone can be used as login ID.</p>
              <div>
                <Label>Role *</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser({...newUser, role: v, pharmacy_id: null, partner_id: null})}>
                  <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="finance">Finance</SelectItem>
                    <SelectItem value="operations">Operations</SelectItem>
                    <SelectItem value="pharmacy">Pharmacy</SelectItem>
                    <SelectItem value="partner">Partner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {newUser.role === 'pharmacy' && (
                <div>
                  <Label>Pharmacy *</Label>
                  <Select value={newUser.pharmacy_id || ''} onValueChange={(v) => setNewUser({...newUser, pharmacy_id: v})}>
                    <SelectTrigger data-testid="new-user-pharmacy"><SelectValue placeholder="Select pharmacy" /></SelectTrigger>
                    <SelectContent>{pharmacies.map(p => <SelectItem key={p.id} value={p.id}>{p.name}{p.branch_name ? ` — ${p.branch_name}` : ''}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {newUser.role === 'partner' && (
                <div>
                  <Label>Partner *</Label>
                  <Select value={newUser.partner_id || ''} onValueChange={(v) => setNewUser({...newUser, partner_id: v})}>
                    <SelectTrigger data-testid="new-user-partner"><SelectValue placeholder="Select partner" /></SelectTrigger>
                    <SelectContent>{partners.map(p => <SelectItem key={p.id} value={p.id}>{p.company_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {['finance', 'operations'].includes(newUser.role) && (
                <div>
                  <Label>Module Permissions</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {ALL_PERMISSIONS.map(perm => (
                      <label key={perm} className="flex items-center gap-2 text-sm">
                        <Checkbox checked={newUser.permissions.includes(perm)} onCheckedChange={() => togglePermission(perm)} />
                        <span className="capitalize">{perm.replace('_', ' ')}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <Button data-testid="submit-invite-btn" onClick={handleInvite} className="w-full" style={{ backgroundColor: '#1E3A8A' }}>Send Invitation</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Login ID</th>
              <th className="text-left p-3">Role</th>
              <th className="text-left p-3">Linked Entity</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id || u.email} data-testid={`user-row-${u.id}`} className="border-t border-slate-200">
                <td className="p-3 font-medium">{u.name}</td>
                <td className="p-3 text-slate-600">{u.email || u.phone}</td>
                <td className="p-3"><span className="capitalize">{u.role}</span></td>
                <td className="p-3 text-slate-700">{u.linked_entity}</td>
                <td className="p-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    u.status === 'active' ? 'bg-green-100 text-green-700' :
                    u.status === 'invited' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-200 text-gray-700'
                  }`}>{(u.status || 'active').toUpperCase()}</span>
                </td>
                <td className="p-3 space-x-2 space-y-1">
                  {u.role !== 'admin' && (
                    <Button
                      data-testid={`temp-password-${u.id}`}
                      onClick={() => { setTempPwDialog({ open: true, user: u, password: '' }); setTempPwResult(null); }}
                      size="sm"
                      variant="outline"
                    >
                      <Key size={14} className="mr-1" /> Set Temp Password
                    </Button>
                  )}
                  {u.status === 'invited' && (
                    <Button data-testid={`resend-invite-${u.id}`} onClick={() => handleResend(u.id)} size="sm" variant="outline">
                      <PaperPlaneRight size={14} className="mr-1" /> Resend
                    </Button>
                  )}
                  {u.role !== 'admin' && (
                    <Button data-testid={`toggle-user-${u.id}`} onClick={() => handleToggle(u)} size="sm" variant="outline">
                      {u.status === 'active' ? <><XCircle size={14} className="mr-1" /> Deactivate</> : <><Check size={14} className="mr-1" /> Activate</>}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-slate-500">No users yet</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={tempPwDialog.open} onOpenChange={(o) => setTempPwDialog(v => ({ ...v, open: o }))}>
        <DialogContent>
          <DialogHeader><DialogTitle>Set Temporary Password — {tempPwDialog.user?.name}</DialogTitle></DialogHeader>
          {!tempPwResult ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">Pass this to the user directly (WhatsApp / in person). They will be forced to change it on next login.</p>
              <div>
                <Label>Temporary password (leave blank to auto-generate)</Label>
                <Input data-testid="temp-pw-input" value={tempPwDialog.password} onChange={e => setTempPwDialog(v => ({ ...v, password: e.target.value }))} placeholder="Auto-generate if empty" />
              </div>
              <Button data-testid="submit-temp-pw" onClick={handleSetTempPassword} className="w-full" style={{ backgroundColor: '#1E3A8A' }}>Set Temporary Password</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-slate-700">Share these credentials with the user:</p>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2 font-mono text-sm" data-testid="temp-pw-result">
                <div><span className="text-slate-500">Login:</span> {tempPwResult.login_id}</div>
                <div><span className="text-slate-500">Password:</span> <strong className="text-blue-800">{tempPwResult.temp_password}</strong></div>
              </div>
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-3 rounded">This is the only time this password is shown. The user will be forced to change it on login.</p>
              <Button onClick={() => setTempPwDialog({ open: false, user: null, password: '' })} className="w-full">Done</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
